import React, { FC, useEffect, useMemo, useState } from 'react';

import { useUnit } from 'effector-react/ssr';

import { FormattedRecipeProduct, RecipeProduct } from '@domains/recipes';

import { addProductsToCartFx } from '@stores/cart';

import { sendAddToCartFromRecipeEvent } from '@utils/analytics';
import { formatAmountWithCurrency } from '@utils/currency';
import { SnackBar } from '@utils/snackbar';
import { getProductsTotalPrice } from '@utils/utils';

import { Button } from '@components/common/Button/Button';
import { Dialog } from '@components/common/Dialog/Dialog';
import { StyledScrollArea } from '@components/common/StyledScrollArea/StyledScrollArea';
import { RecipeBuyProductsDialogItem } from '@components/recipe/RecipeBuyProductsDialogItem/RecipeBuyProductsDialogItem';

import styles from './RecipeBuyProductsDialog.module.scss';

export type BuyRecipeProductsDialogProps = {
    isOpen: boolean;
    initialProducts: RecipeProduct[];
    onOpenChange: (isOpen: boolean) => void;
};

export const BuyRecipeProductsDialog: FC<BuyRecipeProductsDialogProps> = (props) => {
    const { isOpen, initialProducts, onOpenChange } = props;

    const [products, setProducts] = useState<FormattedRecipeProduct[]>([]);
    const [isAddToCartPending, setIsAddToCartPending] = useState<boolean>(false);

    const { addProductsToCart } = useUnit({
        addProductsToCart: addProductsToCartFx,
    });

    const productsTotalPrice = useMemo(
        () => formatAmountWithCurrency(getProductsTotalPrice(products)),
        [products],
    );

    const onAddToCart = async (): Promise<void> => {
        try {
            setIsAddToCartPending(true);
            await addProductsToCart({
                products: products.map((item) => ({
                    product_id: item.product.id,
                    quantity: item.quantity,
                })),
            });
            sendAddToCartFromRecipeEvent(products.map((item) => item.product));
            onOpenChange(false);
            SnackBar.success('Товары добавлены в корзину');
        } catch (error) {
            SnackBar.handleApiError(error);
        } finally {
            setIsAddToCartPending(false);
        }
    };

    const onQuantityChange = (quantity: number, editedItem: FormattedRecipeProduct) => {
        setProducts(
            products.map((item) => {
                if (item.product.id === editedItem.product.id) {
                    return {
                        ...item,
                        quantity,
                    };
                } else return item;
            }),
        );
    };

    const onDeleteItem = (deletedItem: FormattedRecipeProduct) => {
        const newProducts = products.filter((item) => item.product.id !== deletedItem.product.id);

        if (newProducts.length === 0) {
            onOpenChange(false);
            return;
        }

        setProducts(newProducts);
    };

    useEffect(() => {
        setProducts(
            initialProducts.map((item) => ({
                ...item,
                initialQuantity: item.quantity,
            })),
        );
    }, [initialProducts, isOpen]);

    return (
        <Dialog
            title="Купить ингредиенты"
            isOpen={isOpen}
            setOpen={onOpenChange}
            classes={{
                content: styles.content,
                overlay: styles.overlay,
                title: styles.title,
                close: styles.closeButton,
                closeIcon: styles.closeIcon,
            }}
        >
            <div className={styles.wrapper}>
                <StyledScrollArea classes={{ root: styles.scrollArea }}>
                    <ul className={styles.productList}>
                        {products.map((item, index) => (
                            <li key={item.product.slug} className={styles.productListItem}>
                                {index !== 0 && <hr className={styles.separator} />}
                                <RecipeBuyProductsDialogItem
                                    key={item.product.slug}
                                    className={styles.productListItemContent}
                                    item={item}
                                    onChange={(quantity) => onQuantityChange(quantity, item)}
                                    onDelete={() => onDeleteItem(item)}
                                />
                            </li>
                        ))}
                    </ul>
                </StyledScrollArea>
                <div className={styles.totals}>
                    <Button
                        onClick={onAddToCart}
                        isLoading={isAddToCartPending}
                        variant="green"
                        className={styles.addToCartButton}
                    >
                        Добавить всё в&nbsp;корзину
                    </Button>
                    <span className={styles.totalPrice}>{productsTotalPrice}</span>
                </div>
            </div>
        </Dialog>
    );
};
